import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { CatalogsService } from '../catalogs.service'
import { CatalogsRepository } from '../catalogs.repository'
import { Catalog } from '../models/catalog.entity'
import { CategoriesService, Category } from '../../categories'

interface CatalogDetailState {
  catalog?: Catalog
  categories?: Category[]
  uploadUrl?: string
  error?: HttpErrorResponse
  isLoading: boolean
  isModified: boolean
}

const CATALOG_DETAIL_INITIAL_STATE: CatalogDetailState = {
  isLoading: false,
  isModified: false,
}

@Injectable()
export class CatalogDetailStore {
  constructor(
    private catalogsService: CatalogsService,
    private catalogsRepository: CatalogsRepository,
    private categoriesService: CategoriesService,
  ) {}

  private state = signal<CatalogDetailState>(CATALOG_DETAIL_INITIAL_STATE)

  // Selectors
  readonly catalog = computed(() => this.state().catalog)
  readonly categories = computed(() => this.state().categories)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isModified = computed(() => this.state().isModified)
  readonly isErrored = computed(() => !!this.state().error)

  // Effects

  async load(catalogId?: string): Promise<void> {
    if (!catalogId) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.catalogsService.read$(catalogId).subscribe({
        next: async (data) => {
          const categories = await this.getCategories()
          this.setData(
            data,
            this.state().catalog?._id
              ? this.state().catalog?._id !== data._id
              : false,
            categories,
          )
          resolve()
        },
        error: (error) => {
          this.setError(error)
          reject(error)
        },
      })
    })
  }

  async save(catalog: Catalog, notify?: boolean): Promise<boolean> {
    this.unsetError()

    return new Promise((resolve) => {
      this.catalogsService.upsert$(catalog).subscribe({
        next: (data) => {
          this.setData(data, false)
          if (notify === true) {
            this.catalogsRepository.notify({ code: 'CATALOG_SAVED' })
          }
          resolve(true)
        },
        error: (error) => {
          this.setError(error)
          resolve(false)
        },
      })
    })
  }

  async getCategories(): Promise<Category[] | undefined> {
    return new Promise((resolve) => {
      this.categoriesService.list$({ 'parentId:ex': false }, true).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  // Reducers

  setData(catalog: Catalog, isModified?: boolean, categories?: Category[]) {
    this.state.set({
      ...this.state(),
      catalog: catalog,
      categories: categories ?? this.categories(),
      isModified: isModified ?? this.state().isModified,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }
}
